import './RemoteTesting.scss';
import * as Yup from 'yup';
import {
  CANADA,
  COUNTRIESATTOP,
  LANGUAGES,
  SORTBY_OPTIONS,
  USA,
  canProvinces,
  internetSpeeds,
  operatingSystems,
  peripherals,
  specifications
} from '../../utils/constants';
import {
  selectAllCountries,
  selectEnums,
  selectIsFetchingAllCountries,
  selectIsFetchingEnums
} from '../../ducks/entities';
import { useEffect, useState } from 'react';
import SelectQuestion from '../SelectQuestion/SelectQuestion';
import YesNoButton from '../YesNoButton/YesNoButton';
import { createLocalizedOptions } from '../../pages/root/App';
import { createOptions } from '../../utils/generic';
import iconHome from '../../assets/images/ubisoft-playtest-icon-home-black.webp';
import messages from './messages';
import { selectLanguageSettings } from '../../ducks/settings';
import siteMessages from '../../pages/root/siteMessages';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

interface Props {
  isInterested: boolean | null;
  values: any;
  errors: any;
  setFieldValue: any;
  updateSchema: (value: any) => void;
  withHeader?: boolean;
}

const RemoteTesting = ({
  isInterested,
  values,
  errors,
  setFieldValue,
  updateSchema,
  withHeader = true
}: Props) => {
  const intl = useIntl();
  const [interested, setInterested] = useState(isInterested);
  const lang = useSelector(selectLanguageSettings);
  const countries = useSelector(selectAllCountries);
  const isFetchingAllCountries = useSelector(selectIsFetchingAllCountries);
  const [countriesOptions, setCountriesOptions] = useState<any>();
  const [selectedCountry, setSelectedCountry] = useState(values.countryCode);
  const enums = useSelector(selectEnums);
  const isFetchingEnums = useSelector(selectIsFetchingEnums);
  const [usaProvincesOptions, setUsaProvincesOptions] = useState<any>();

  const canProvincesOptions = createLocalizedOptions(canProvinces, messages);
  const peripheralsOptions = createLocalizedOptions(peripherals, messages);
  const internetSpeedsOptions = createLocalizedOptions(
    internetSpeeds,
    messages
  );
  const operatingSystemsOptions = createLocalizedOptions(
    operatingSystems,
    messages,
    SORTBY_OPTIONS.Name
  );
  const specificationsOptions = createLocalizedOptions(
    specifications,
    messages
  );

  useEffect(() => {
    if (isFetchingAllCountries || !countries) return;

    const countriesWithIndex = countries.map((country) => ({
      value: country.countryCode,
      label:
        +lang <= 3
          ? lang ===
            LANGUAGES.find((l) => l.name.toLowerCase() === 'english')?.id
            ? country.name
            : country.localizations.find(
                (l) =>
                  l.locale ===
                  LANGUAGES.find((l) => l.id === lang)
                    ?.name.substring(0, 3)
                    .toLowerCase()
              )?.text
          : lang === 6
          ? country.localizations.find((l) => l.locale === 'fra')?.text
          : country.name,
      index: COUNTRIESATTOP.find(
        (c: any) => c.countryCode === country.countryCode
      )?.index
    }));

    countriesWithIndex.sort((a: any, b: any) => {
      const aAtTop = COUNTRIESATTOP.some((c) => c.countryCode == a.value);
      const bAtTop = COUNTRIESATTOP.some((c) => c.countryCode == b.value);

      return aAtTop && !bAtTop
        ? -1
        : !aAtTop && bAtTop
        ? 1
        : aAtTop && bAtTop
        ? a.index - b.index
        : a.label - b.label;
    });

    setCountriesOptions(countriesWithIndex);
  }, [countries]);

  useEffect(() => {
    setSelectedCountry(values.countryCode);
  }, [values.countryCode]);

  useEffect(() => {
    if (!isFetchingEnums && enums) {
      const provinces = enums.provinces.filter(
        (p: any) => p.countryCode == USA
      );
      setUsaProvincesOptions(createOptions(provinces));
    }
  }, [enums]);

  useEffect(() => {
    const remoteTestingSchema = {
      countryCode: Yup.string().required(
        intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
      ),
      provinceId: Yup.number()
        .when('countryCode', {
          is: (val: string) => val == CANADA,
          then: () =>
            Yup.number()
              .min(53, intl.formatMessage({ ...siteMessages.FIELD_REQUIRED }))
              .max(65, intl.formatMessage({ ...siteMessages.FIELD_REQUIRED }))
              .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED }))
        })
        .when('countryCode', {
          is: (val: string) => val == USA,
          then: () =>
            Yup.number()
              .min(1, intl.formatMessage({ ...siteMessages.FIELD_REQUIRED }))
              .max(52, intl.formatMessage({ ...siteMessages.FIELD_REQUIRED }))
              .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED }))
        }),
      peripheralIds: Yup.array(Yup.number())
        .min(1, intl.formatMessage({ ...siteMessages.FIELD_REQUIRED }))
        .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })),
      internetSpeedId: Yup.number().required(
        intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
      ),
      operatingSystemId: Yup.number().required(
        intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
      ),
      specificationId: Yup.number().required(
        intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
      )
    };
    updateSchema(remoteTestingSchema);
  }, []);

  return (
    <div className="remote-testing">
      {withHeader && (
        <div className="header">
          <img src={iconHome} alt="" className="mt-n1" />
          <h3>{intl.formatMessage({ ...messages.headerRemoteTesting })}</h3>
        </div>
      )}
      <div className="content">
        <YesNoButton
          id="candidateRemote"
          name="remoteTesting"
          label={intl.formatMessage({ ...messages.remoteTesting })}
          value={isInterested}
          onChange={(value: boolean) => {
            setInterested(value);
            setFieldValue('remoteTesting', value);
          }}
        />
        {interested && (
          <>
            <SelectQuestion
              prefix="candidateRemote"
              controlId="countryCode"
              label={intl.formatMessage({ ...messages.countries })}
              isMulti={false}
              options={countriesOptions}
              value={values.countryCode ?? []}
              error={typeof errors == 'string' ? errors : errors?.countryCode}
              setFieldValue={setFieldValue}
            />
            {selectedCountry === CANADA && (
              <SelectQuestion
                prefix="candidateRemote"
                controlId="provinceId"
                label={intl.formatMessage({ ...messages.provinces })}
                isMulti={false}
                options={canProvincesOptions}
                value={values.provinceId ?? []}
                error={typeof errors == 'string' ? errors : errors?.provinceId}
                setFieldValue={setFieldValue}
              />
            )}
            {selectedCountry === USA && (
              <SelectQuestion
                prefix="candidateRemote"
                controlId="provinceId"
                label={intl.formatMessage({ ...messages.provinces })}
                isMulti={false}
                options={usaProvincesOptions}
                value={values.provinceId ?? []}
                error={typeof errors == 'string' ? errors : errors?.provinceId}
                setFieldValue={setFieldValue}
              />
            )}
            <SelectQuestion
              prefix="candidateRemote"
              controlId="peripheralIds"
              label={intl.formatMessage({ ...messages.peripherals })}
              isMulti={true}
              options={peripheralsOptions}
              value={values.peripheralIds ?? []}
              error={typeof errors == 'string' ? errors : errors?.peripheralIds}
              setFieldValue={setFieldValue}
            />
            <SelectQuestion
              prefix="candidateRemote"
              controlId="internetSpeedId"
              label={intl.formatMessage({ ...messages.internetSpeeds })}
              isMulti={false}
              options={internetSpeedsOptions}
              value={values.internetSpeedId ?? []}
              error={
                typeof errors == 'string' ? errors : errors?.internetSpeedId
              }
              setFieldValue={setFieldValue}
            />
            <SelectQuestion
              prefix="candidateRemote"
              controlId="operatingSystemId"
              label={intl.formatMessage({ ...messages.operatingSystems })}
              isMulti={false}
              options={operatingSystemsOptions}
              value={values.operatingSystemId ?? []}
              error={
                typeof errors == 'string' ? errors : errors?.operatingSystemId
              }
              setFieldValue={setFieldValue}
            />
            <SelectQuestion
              prefix="candidateRemote"
              controlId="specificationId"
              label={intl.formatMessage({ ...messages.specifications })}
              isMulti={false}
              options={specificationsOptions}
              value={values.specificationId ?? []}
              error={
                typeof errors == 'string' ? errors : errors?.specificationId
              }
              setFieldValue={setFieldValue}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default RemoteTesting;
